import { useState } from "react";
import { Check, ChevronsUpDown, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useTesters } from "@/hooks/useTesters";
import type { TesterUser } from "@/services/testers";

import { getInitials, getAvatarColor } from "@/lib/avatar";

interface TesterSwitcherProps {
  selectedTesterId: string | undefined;
  onSelect: (tester: TesterUser | null) => void;
}

export function TesterSwitcher({ selectedTesterId, onSelect }: TesterSwitcherProps) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useTesters({ enabled: open });

  const testers = data?.testers ?? [];
  const selectedTester = testers.find((t) => t.id === selectedTesterId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[260px] justify-between"
        >
          {selectedTester ? (
            <span className="flex items-center gap-2 truncate">
              <Avatar className="h-5 w-5">
                <AvatarFallback className={cn("text-[10px]", getAvatarColor(selectedTester.name))}>
                  {getInitials(selectedTester.name)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">{selectedTester.name}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2 text-muted-foreground">
              <User className="h-4 w-4" />
              View as tester...
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search testers..." />
          <CommandList>
            <CommandEmpty>
              {isLoading ? "Loading testers..." : "No testers found."}
            </CommandEmpty>

            {selectedTesterId && (
              <>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      onSelect(null);
                      setOpen(false);
                    }}
                  >
                    <User className="mr-2 h-4 w-4" />
                    My Dashboard
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            <CommandGroup heading="Testers">
              {testers.map((tester) => (
                <CommandItem
                  key={tester.id}
                  value={`${tester.name} ${tester.email}`}
                  onSelect={() => {
                    onSelect(tester);
                    setOpen(false);
                  }}
                >
                  <Avatar className="mr-2 h-6 w-6">
                    <AvatarFallback className={cn("text-[10px]", getAvatarColor(tester.name))}>
                      {getInitials(tester.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col min-w-0">
                    <span className="truncate text-sm">{tester.name}</span>
                    <span className="truncate text-xs text-muted-foreground">{tester.email}</span>
                  </div>
                  {selectedTesterId === tester.id && (
                    <Check className="ml-auto h-4 w-4 shrink-0" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
